import { useMemo } from "react";
import { useAbility } from "@casl/react";
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography, useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {format} from '@react-input/number-format';

import DataLoadingError from '@components/common/DataLoadingError';
import useSessionParticipantsQuery from "@components/common/hooks/api/useSessionParticipantsQuery";
import { getMaxSessionParticipants } from "@components/Pages/Events/eventFunctions";
import {
  getParticipantName,
  getSessionShortDate,
  participantAttendedSession,
  sortByParticipantName,
  orderSessions,
} from '@components/Pages/Events/Sessions/sessionFunctions';
import { AbilityContext, Subjects } from "@lib/permissions";
import { getOptionLabel } from "@lib/utils";
import { eventDemographics, NILL } from "@lib/constants";

const EventParticipantsTable = ({ event, sessions }) => {
  const theme = useTheme();
  const ability = useAbility(AbilityContext);

  const { data: allParticipants, ...participantsQuery } = useSessionParticipantsQuery(event.id);

  const uniqueParticipants = useMemo(() => {
    if (!event) {
      return '';
    }
    return getMaxSessionParticipants(event);
  }, [event]);

  // This table shows a column for each completed session, ordered by start date. Canceled sessions are not shown.
  const visibleSessions = orderSessions(sessions.filter(s => s.status !== 'CANCELED'));

  // What these variables mean:
  // allParticipants = everyone in the participants table with the matching event ID. They may not have attended any sessions.
  // participants = only people who attended at least one session.
  const participants = allParticipants.filter(p => p.participantAttendance.some(pa => pa.status === 'PRESENT'));
  participants.sort(sortByParticipantName);

  const isLoading = participantsQuery.isLoading;

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign="center">
        <CircularProgress/>
      </Grid>
    );
  }

  if (participantsQuery.isError && participantsQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load participants!'} errors={participantsQuery.error.errors}/>;
  }

  // Has any participant data been recorded for the event?
  const eventHasParticipants = participants?.length > 0;

  return (
    <>
      <Typography variant="body1" sx={{ fontFamily: 'Montserrat', marginTop: '38px', marginBottom: '24px' }}>
          <span style={{ fontWeight: 600, fontSize: theme.typography.pxToRem(22) }}>
            Total Participants for this event: {uniqueParticipants || '-'}
          </span>
      </Typography>

      <div style={{ marginTop: '32px', marginBottom: '24px' }} data-cy="participants-section">

        {/* Participants/Sessions table. Visible to staff. */}
        {eventHasParticipants && (
          <>
            <DataHeading variant="h4">ATTENDANCE</DataHeading>
            <HorizontalScroller>
              <Table sx={{ width: visibleSessions.length > 5 ? '100%' : 'auto' }} data-cy="participants-table">
                <TableHead>
                  <TableHeadRow>
                    <TableCell component="th">Name</TableCell>
                    <TableCell component="th" style={{ textAlign: 'center' }}>Demographics<br/>Collected?</TableCell>
                    {visibleSessions.map((session, idx) => (
                      <TableCell key={session.id} component="th" style={{ textAlign: 'center' }}>
                        {idx + 1}<br/>({getSessionShortDate(session)})
                      </TableCell>
                    ))}
                  </TableHeadRow>
                </TableHead>
                <TableBody>
                  {participants.map(participant => (
                    <TableRow key={participant.id}>
                      <NameCell>
                        {getParticipantName(participant)}
                      </NameCell>
                      <DataCell>
                        {participant.demographics_collected ?
                          (
                            <Tooltip title="Collected">
                              <CheckCircleIcon color="success"/>
                            </Tooltip>)
                          :
                          (
                            <Tooltip title="Not collected">
                              <CancelRoundedIcon color="error"/>
                            </Tooltip>
                          )}
                      </DataCell>

                      {visibleSessions.map(session => (
                        <DataCell key={session.id} sx={{ width: visibleSessions.length < 6 ? '120px' : null }}>
                          <AttendanceIcon session={session} participant={participant}/>
                        </DataCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </HorizontalScroller>
          </>
        )}

        {/* Admin-only summary table */}
        {ability.can('view_all', Subjects.EVENT) && (
          <>
            <DataHeading variant="h4">DEMOGRAPHICS</DataHeading>
            <HorizontalScroller>
              <Table data-cy="participants-summary-table">
                <TableHead>
                  <TableHeadRow>
                    <TableCell component="th">Name</TableCell>
                    <TableCell component="th" style={{ textAlign: 'center' }}>Total<br/>Sessions</TableCell>
                    <TableCell component="th" style={{ textAlign: 'center' }}>Total<br/>Hours</TableCell>
                    <TableCell component="th" style={{ textAlign: 'center' }}>Age Group<br/>(Years)</TableCell>
                    <TableCell component="th" style={{ textAlign: 'center' }}>Gender</TableCell>
                    <TableCell component="th" style={{ textAlign: 'left' }}>Race & Ethnicity</TableCell>
                  </TableHeadRow>
                </TableHead>
                <TableBody>
                  {/* TODO: hide people who didn't attend any session */}
                  {participants.map(participant => (
                    <TableRow key={participant.id}>
                      <NameCell>
                        {getParticipantName(participant)}
                      </NameCell>
                      <DataCell>
                        {participant.totals.sessions_attended}
                      </DataCell>
                      <DataCell>
                        {format(participant.totals.total_hours, {
                          format: 'decimal',
                          locales: 'en',
                          maximumFractionDigits: 2
                        })}
                      </DataCell>
                      <DataCell>
                        {participant.age ? getOptionLabel(eventDemographics.ageRange, participant.age).replace(' years', '') : NILL}
                      </DataCell>
                      <DataCell>
                        {participant.gender ? getOptionLabel(eventDemographics.gender, participant.gender) : NILL}
                      </DataCell>
                      <DataCell style={{ padding: '4px 10px', textAlign: 'left' }}>
                        {participant.raceEthnicity && Array.isArray(participant.raceEthnicity) ?
                           participant.raceEthnicity.map(re => getOptionLabel(eventDemographics.raceEthnicity, re)).join(', ') :
                           NILL
                        }
                      </DataCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </HorizontalScroller>
          </>
        )}

        {!eventHasParticipants && (
          <Typography variant="body1" data-cy="participants-empty">
            There are no participants.
          </Typography>
        )}
      </div>
    </>
  );
};

const HorizontalScroller = styled('div')({
  maxWidth: '100%',
  overflowX: 'auto',
  marginBottom: '24px'
});

const DataHeading = styled(Typography)({
  fontSize: '16px',
  lineHeight: '40px',
});

const TableHeadRow = styled(TableRow)({
  '& .MuiTableCell-root': {
    backgroundColor: '#f4f4f4',
    borderColor: '#0000001f',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    padding: '5px 10px',
  },
});

const NameCell = styled(TableCell)({
  backgroundColor: '#f4f4f4',
  borderColor: '#0000001f',
  fontSize: '16px',
  minWidth: '200px',
  padding: '10px',
});

const DataCell = styled(TableCell)({
  backgroundColor: '#ffffff',
  border: '1px solid #0000001f',
  fontSize: '16px',
  textAlign: 'center',
  padding: '4px',
  '& > svg': {
    marginTop: '4px',
  }
});

const AttendanceIcon = ({ session, participant }) => {
  // Note: We don't show the "nill" here if they didn't attend. That appears on the session detail page as
  // a signal that data needs to be updated. Here, the event is completed, so there's nothing to edit.
  // If not "PRESENT", assume the person was absent.
  if (participantAttendedSession(participant, session)) {
    return (
      <Tooltip title="Attended">
        <CheckCircleIcon color="success"/>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Did not attend">
      <CancelRoundedIcon color="error"/>
    </Tooltip>
  );
}

export default EventParticipantsTable;
